import { LuminaireAnalytics, MonthlyAnalyticsPoint } from '../../../types/analytics';

interface Series {
  analytics: LuminaireAnalytics;
  color: string;
  label: string;
}

interface SparklineStripProps {
  primary: Series;
  secondary?: Series;
}

interface SparkSpec {
  key: keyof MonthlyAnalyticsPoint;
  label: string;
  type: 'value' | 'count';
}

const SPARKS: SparkSpec[] = [
  { key: 'compatibilityScoreAvg', label: 'Compatibility', type: 'value' },
  { key: 'matchFrequencyTotal', label: 'Match Frequency', type: 'count' },
  { key: 'applyCount', label: 'Applies', type: 'count' },
  { key: 'favoriteCount', label: 'Favorites', type: 'count' },
  { key: 'inquiriesGenerated', label: 'Inquiries', type: 'count' },
];

const buildPath = (values: number[], width: number, height: number, padding: number): string => {
  if (!values.length) return '';
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = (width - padding * 2) / Math.max(1, values.length - 1);
  return values
    .map((v, i) => {
      const x = padding + i * stepX;
      const y = padding + (1 - (v - min) / range) * (height - padding * 2);
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
};

const buildAreaPath = (values: number[], width: number, height: number, padding: number): string => {
  if (!values.length) return '';
  const linePath = buildPath(values, width, height, padding);
  const stepX = (width - padding * 2) / Math.max(1, values.length - 1);
  const lastX = padding + (values.length - 1) * stepX;
  return `${linePath} L ${lastX.toFixed(1)} ${(height - padding).toFixed(1)} L ${padding.toFixed(1)} ${(height - padding).toFixed(1)} Z`;
};

const formatMonthShort = (iso: string): string => {
  // YYYY-MM → "Mar"
  const [, m] = iso.split('-');
  const idx = Math.max(0, Math.min(11, parseInt(m, 10) - 1));
  return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][idx];
};

const SparkCard = ({ spec, primary, secondary }: { spec: SparkSpec; primary: Series; secondary?: Series }) => {
  const seriesA = primary.analytics.monthlyTimeSeries;
  const seriesB = secondary?.analytics.monthlyTimeSeries;
  const valsA = seriesA.map((p) => p[spec.key] as number);
  const valsB = seriesB?.map((p) => p[spec.key] as number);
  const last = valsA[valsA.length - 1] ?? 0;
  // Trailing 4-month delta — what the user asked to surface
  const lastFour = valsA.slice(-4);
  const baseline = lastFour[0] ?? 0;
  const tip = lastFour[lastFour.length - 1] ?? 0;
  const trailingDelta = baseline === 0 ? 0 : ((tip - baseline) / baseline) * 100;

  const W = 480;
  const H = 140;
  const P = 14;
  const min = Math.min(...valsA, ...(valsB ?? []));
  const max = Math.max(...valsA, ...(valsB ?? []));
  const range = max - min || 1;
  const stepX = (W - P * 2) / Math.max(1, valsA.length - 1);

  // Highlight band for last 4 months
  const lastFourStartX = P + (valsA.length - 4) * stepX;

  return (
    <div className="rounded-xl border border-greyP4 bg-blackP1/40 p-4">
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-greyP2 font-semibold">{spec.label}</div>
          <div className="text-2xl font-bold text-white mt-0.5 tabular-nums">
            {spec.type === 'value' ? last.toFixed(1) : Math.round(last).toLocaleString()}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-greyP3 font-semibold">Last 4 mo.</div>
          <div className="text-sm font-bold tabular-nums" style={{ color: trailingDelta >= 0 ? '#B3EC49' : '#B3B3B3' }}>
            {trailingDelta >= 0 ? '▲' : '▼'} {Math.abs(trailingDelta).toFixed(0)}%
          </div>
        </div>
      </div>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block">
        {/* horizontal grid lines */}
        {[0.25, 0.5, 0.75].map((frac) => (
          <line
            key={frac}
            x1={P}
            y1={P + frac * (H - P * 2)}
            x2={W - P}
            y2={P + frac * (H - P * 2)}
            stroke="#2A2A33"
            strokeWidth={1}
            strokeDasharray="2 4"
          />
        ))}

        {/* Last-4-months highlight band */}
        <rect
          x={lastFourStartX}
          y={P - 4}
          width={W - P - lastFourStartX}
          height={H - P * 2 + 8}
          fill={primary.color}
          fillOpacity={0.04}
        />
        <line
          x1={lastFourStartX}
          y1={P - 4}
          x2={lastFourStartX}
          y2={H - P + 4}
          stroke={primary.color}
          strokeOpacity={0.25}
          strokeWidth={1}
          strokeDasharray="3 3"
        />

        {/* Slot B (secondary) underneath */}
        {valsB && (
          <path
            d={buildPath(valsB, W, H, P)}
            fill="none"
            stroke={secondary!.color}
            strokeWidth={1.5}
            strokeOpacity={0.75}
            strokeDasharray="4 3"
          />
        )}

        {/* Slot A area + line */}
        <path d={buildAreaPath(valsA, W, H, P)} fill={primary.color} fillOpacity={0.1} />
        <path d={buildPath(valsA, W, H, P)} fill="none" stroke={primary.color} strokeWidth={2} />

        {/* Dots on each month for Slot A */}
        {valsA.map((v, i) => {
          const x = P + i * stepX;
          const y = P + (1 - (v - min) / range) * (H - P * 2);
          const inLastFour = i >= valsA.length - 4;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={inLastFour ? 3 : 2}
              fill={primary.color}
              stroke="#0E0E12"
              strokeWidth={1}
            />
          );
        })}

        {/* Month labels under x-axis */}
        {seriesA.map((p, i) => {
          const x = P + i * stepX;
          const showLabel = i % 2 === 0 || i >= valsA.length - 4;
          if (!showLabel) return null;
          const inLastFour = i >= valsA.length - 4;
          return (
            <text
              key={p.month}
              x={x}
              y={H - 2}
              textAnchor="middle"
              fontSize={9}
              fontFamily="AzbukaPro, sans-serif"
              fill={inLastFour ? primary.color : '#747474'}
              fontWeight={inLastFour ? 600 : 400}
            >
              {formatMonthShort(p.month)}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

export const SparklineStrip = ({ primary, secondary }: SparklineStripProps) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    {SPARKS.map((spec) => (
      <SparkCard key={spec.key} spec={spec} primary={primary} secondary={secondary} />
    ))}
  </div>
);
